import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Download, Loader2, RefreshCw, TrendingUp, Users, Target, Activity } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/config/api";
import { downloadCsv } from "@/lib/exportCsv";

const KpiCard = ({ label, value, hint, icon: Icon, tone, testId }) => (
  <Card data-testid={testId}>
    <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
      <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
      <Icon className={`h-4 w-4 ${tone}`} />
    </CardHeader>
    <CardContent>
      <div className="text-3xl font-bold tracking-tight">{value}</div>
      <p className="text-xs text-muted-foreground mt-1">{hint}</p>
    </CardContent>
  </Card>
);

export default function InsightsPage() {
  const [loading, setLoading] = useState(true);
  const [tracking, setTracking] = useState(null);
  const [conversion, setConversion] = useState(null);
  const [eod, setEod] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [trackingRes, conversionRes, eodRes, alertsRes] = await Promise.all([
        axios.get(`${API}/reports/referral-tracking`, { withCredentials: true }),
        axios.get(`${API}/reports/appointment-vs-visits`, { withCredentials: true }),
        axios.get(`${API}/reports/eod-snapshot`, { withCredentials: true }),
        axios.get(`${API}/doctor-alerts`, { withCredentials: true }),
      ]);
      setTracking(trackingRes.data);
      setConversion(conversionRes.data);
      setEod(eodRes.data);
      setAlerts(Array.isArray(alertsRes.data) ? alertsRes.data : []);
    } catch (error) {
      console.error("Insights load failed", error);
      toast.error("Failed to load insights");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const exportAlerts = () => {
    downloadCsv(
      `doctors-not-referring-${new Date().toISOString().split("T")[0]}`,
      ["Doctor", "Doctor ID", "Last visit", "Last referral", "Flagged on"],
      alerts.map((a) => [a.doctor_name, a.doctor_id, a.last_visit_date, a.last_referral_date || "Never", a.alert_triggered_date])
    );
  };

  const repVisits = Object.entries(eod?.total_visits_per_rep || {});
  const totalVisits = repVisits.reduce((sum, [, count]) => sum + count, 0);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="insights-page">
      <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Insights</h1>
          <p className="text-sm text-muted-foreground">Pipeline performance, forecast and doctors needing attention</p>
        </div>
        <Button variant="outline" onClick={fetchAll} disabled={loading} className="gap-2" data-testid="insights-refresh-btn">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24" data-testid="insights-loading">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="px-6 pb-10 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              label="Total Referrals"
              value={tracking?.total_referrals ?? 0}
              hint="All referrals on record"
              icon={TrendingUp}
              tone="text-blue-600"
              testId="kpi-total-referrals"
            />
            <KpiCard
              label="New Doctors Today"
              value={eod?.new_doctors_today ?? 0}
              hint={`${eod?.doctors_referring_today ?? 0} doctors referred today`}
              icon={Users}
              tone="text-emerald-600"
              testId="kpi-new-doctors"
            />
            <KpiCard
              label="Forecasted Month"
              value={eod?.forecasted_month_referrals ?? 0}
              hint={`${eod?.current_month_referrals_to_date ?? 0} so far · ${eod?.working_days_in_month ?? 0} working days`}
              icon={Target}
              tone="text-violet-600"
              testId="kpi-forecast"
            />
            <KpiCard
              label="Visit Conversion"
              value={`${(conversion?.conversion_rate_percent ?? 0).toFixed(1)}%`}
              hint={`${conversion?.total_referrals ?? 0} referrals from ${conversion?.total_visits ?? 0} visits`}
              icon={Activity}
              tone="text-amber-600"
              testId="kpi-conversion"
            />
          </div>

          <Card className={alerts.length ? "border-red-200 bg-red-50/60" : ""} data-testid="alerts-card">
            <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
              <div>
                <CardTitle className="flex items-center gap-2 text-base">
                  <AlertTriangle className={`h-4 w-4 ${alerts.length ? "text-red-600" : "text-muted-foreground"}`} />
                  Doctors Not Referring (6+ weeks)
                </CardTitle>
                <CardDescription>
                  {alerts.length
                    ? `${alerts.length} visited doctor(s) have not sent a referral in over six weeks`
                    : "Every recently visited doctor has referred within the last six weeks"}
                </CardDescription>
              </div>
              {alerts.length > 0 && (
                <Button variant="outline" size="sm" className="gap-2" onClick={exportAlerts} data-testid="alerts-export-btn">
                  <Download className="h-4 w-4" />
                  Export CSV
                </Button>
              )}
            </CardHeader>
            {alerts.length > 0 && (
              <CardContent>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {alerts.map((alert) => (
                    <div
                      key={alert.alert_id}
                      className="flex items-start justify-between gap-4 bg-white border border-red-200 rounded-lg p-3"
                      data-testid={`alert-row-${alert.doctor_id}`}
                    >
                      <div>
                        <p className="font-medium text-sm">{alert.doctor_name}</p>
                        <p className="text-xs text-muted-foreground">Last visit: {alert.last_visit_date || "—"}</p>
                        <p className="text-xs text-red-600">Last referral: {alert.last_referral_date || "Never"}</p>
                      </div>
                      <Badge variant="destructive">Flagged {alert.alert_triggered_date}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>

          {eod && (
            <Card data-testid="eod-summary-card">
              <CardHeader>
                <CardTitle className="text-base">Today at a glance</CardTitle>
                <CardDescription>
                  {eod.snapshot_date} · {eod.source === "snapshot" ? "stored snapshot" : "live calculation"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Referrals today</p>
                    <p className="text-2xl font-bold" data-testid="eod-referrals-today">{eod.total_referrals_today}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Month to date</p>
                    <p className="text-2xl font-bold">{eod.current_month_referrals_to_date}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Avg per working day</p>
                    <p className="text-2xl font-bold">{eod.average_referrals_per_working_day}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Visits logged today</p>
                    <p className="text-2xl font-bold">{totalVisits}</p>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-3">Visits per rep</p>
                  {repVisits.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No visits captured today yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {repVisits.map(([rep, visits]) => (
                        <div key={rep} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                          <div>
                            <p className="text-sm font-medium">{rep}</p>
                            <p className="text-xs text-muted-foreground">
                              {eod.first_input_time_per_rep?.[rep] || "—"} → {eod.last_input_time_per_rep?.[rep] || "—"}
                            </p>
                          </div>
                          <span className="text-sm font-semibold">{visits} visits</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
